/**
 * Rosalie's breathing — spec §8: "Rosalie's breathing is the tension meter — the
 * only HUD the game needs. Fast when seen, held when hiding, ragged when running."
 *
 * This is the most important sound in the game. It is the entire stealth UI: the
 * player learns their exposure through her lungs rather than through a meter, and
 * that is why there is no meter.
 *
 * Built as one generator over a `tension` parameter 0..1 so the states are points
 * on a continuum rather than separate clips — the transitions have to be smooth or
 * the illusion breaks. Registered at a few sample points for audition.
 *
 * **This is a first pass, deliberately.** It proves the parameterisation and the
 * measurement path. The final voicing is character work and belongs in a session
 * that can hear it against a room. Re-voice it; do not treat it as locked.
 */

import { noiseSource, filter, gain, connectChain } from '../synth';
import type { SoundDefinition } from '../types';

interface BreathSpec {
  /** Breaths per second. Calm ~0.35, ragged ~1.1. */
  rate: number;
  /** Peak amplitude of each breath. */
  depth: number;
  /** Bandpass centre — rises with effort while retaining a low animal body. */
  centre: number;
  /** Low glottal carrier level, shaped by the same breath envelope as the air. */
  voice: number;
  /** Glottal carrier pitch — present as body, not a vocalisation. */
  voicePitch: number;
  /** How much of the cycle is the inhale, 0..1. Panic shortens the exhale. */
  inhaleShare: number;
  /** Extra broadband rasp, added when running. */
  rasp: number;
}

function specFor(tension: number): BreathSpec {
  const t = Math.min(Math.max(tension, 0), 1);
  return {
    rate: 0.35 + t * 0.75,
    depth: 0.1 + t * 0.16,
    centre: 360 + t * 540,
    voice: 0.014 + t * 0.006,
    voicePitch: 96 + t * 24,
    inhaleShare: 0.45 - t * 0.12,
    rasp: t * t * 0.05,
  };
}

function buildBreath(tension: number, duration: number) {
  return (ctx: BaseAudioContext): void => {
    const spec = specFor(tension);

    // The quiet voiced carrier adds body, so trim the master gain to retain the
    // existing headroom at the noisiest end of the tension range.
    const out = gain(ctx, 0.72);
    out.connect(ctx.destination);

    // Air band — low enough to read as breath rather than a rustling sweep.
    const body = gain(ctx, 0);
    connectChain(
      noiseSource(ctx, duration, 71),
      filter(ctx, { type: 'bandpass', frequency: spec.centre, q: 0.9 }),
      body,
      out,
    );

    // A filtered, low glottal carrier gives the air a living body without ever
    // becoming a voiced call. Its gain follows the same continuous envelope.
    const voice = gain(ctx, 0);
    const voiceOsc = ctx.createOscillator();
    voiceOsc.type = 'sawtooth';
    voiceOsc.frequency.value = spec.voicePitch;
    connectChain(
      voiceOsc,
      filter(ctx, { type: 'lowpass', frequency: 340, q: 0.7 }),
      voice,
      out,
    );
    voiceOsc.start(0);

    // Rasp — only meaningful at high tension.
    const rasp = gain(ctx, 0);
    if (spec.rasp > 0.001) {
      connectChain(
        noiseSource(ctx, duration, 97),
        filter(ctx, { type: 'highpass', frequency: 1800, q: 0.6 }),
        rasp,
        out,
      );
    }

    const period = 1 / spec.rate;
    const t0 = ctx.currentTime;

    for (let t = 0; t < duration - 0.001; t += period) {
      const inhale = period * spec.inhaleShare;
      const exhale = period - inhale;

      // Inhale: quicker attack, slightly brighter.
      body.gain.setValueAtTime(0, t0 + t);
      body.gain.linearRampToValueAtTime(spec.depth, t0 + t + inhale * 0.55);
      body.gain.linearRampToValueAtTime(0.012, t0 + t + inhale);
      voice.gain.setValueAtTime(0, t0 + t);
      voice.gain.linearRampToValueAtTime(spec.voice, t0 + t + inhale * 0.55);
      voice.gain.linearRampToValueAtTime(spec.voice * 0.55, t0 + t + inhale);

      // Exhale: longer, softer, falls away.
      body.gain.linearRampToValueAtTime(spec.depth * 0.68, t0 + t + inhale + exhale * 0.3);
      body.gain.linearRampToValueAtTime(0, t0 + t + period);
      voice.gain.linearRampToValueAtTime(
        spec.voice * 0.42,
        t0 + t + inhale + exhale * 0.3,
      );
      voice.gain.linearRampToValueAtTime(0, t0 + t + period);

      if (spec.rasp > 0.001) {
        rasp.gain.setValueAtTime(0, t0 + t);
        rasp.gain.linearRampToValueAtTime(spec.rasp, t0 + t + inhale * 0.5);
        rasp.gain.linearRampToValueAtTime(0, t0 + t + inhale);
      }
    }

    // Guarantee we land on zero so the loop seam is clean.
    body.gain.setValueAtTime(0, t0 + duration);
    voice.gain.setValueAtTime(0, t0 + duration);
    rasp.gain.setValueAtTime(0, t0 + duration);
  };
}

function breath(id: string, label: string, description: string, tension: number): SoundDefinition {
  // Whole number of breath cycles, so the loop is seamless.
  const period = 1 / specFor(tension).rate;
  const cycles = Math.max(Math.round(6 / period), 2);
  return {
    id,
    label,
    description,
    category: 'creature',
    duration: cycles * period,
    loop: true,
    render: buildBreath(tension, cycles * period),
  };
}

export const breathSounds: readonly SoundDefinition[] = [
  breath(
    'rosalie-breath-calm',
    'Rosalie — calm (tension 0.0)',
    'Unobserved. Slow, soft, almost inaudible. The player should stop noticing it.',
    0.0,
  ),
  breath(
    'rosalie-breath-alert',
    'Rosalie — alert (tension 0.5)',
    'Seen, or nearly. Faster and brighter. The first thing the player reacts to.',
    0.5,
  ),
  breath(
    'rosalie-breath-ragged',
    'Rosalie — ragged (tension 1.0)',
    'Running. Fast, rasping, short exhales. Should be genuinely unpleasant.',
    1.0,
  ),
];
